import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'

import { selectMiscItems, useUpdateMiscItemsMutation } from '@/app/services/miscItems'
import { selectQuote, useUpdateQuoteMutation } from '@/app/services/quotes'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { useMutationErrorSnackbar } from '@/common/hooks'
import { getSortFunction } from '@/common/utils/quoteUtilities'

const MiscItemListSort = () => {
    const { t } = useTranslation()
    const notifyMutationError = useMutationErrorSnackbar()
    const { quoteId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const selectedQuote = useSelector((state) => selectQuote(state, { organisationId: organisationId, quoteId }))
    const miscItems = useSelector((state) => selectMiscItems(state, { organisationId: organisationId, quoteId }))

    const [selectedCriteria, setSelectedCriteria] = useState(selectedQuote.sortMiscItemsBy ?? 'Custom')
    const [selectedDirection, setSelectedDirection] = useState(selectedQuote.sortMiscItemsAsc ? 'ASC' : 'DESC')

    const [updateQuote] = useUpdateQuoteMutation()
    const [updateMiscItems] = useUpdateMiscItemsMutation()

    const handleUpdateQuote = async (updatedFields) => {
        //dispatch(setSelectedQuoteStatus(QuoteStatus.NotCalculated))
        try {
            await updateQuote({
                organisationId,
                quoteId,
                quote: {
                    ...selectedQuote,
                    ...updatedFields,
                },
            }).unwrap()

            return true
        } catch (error) {
            notifyMutationError(error, t('$t(An error occurred) updating the $t(quote).'))

            return false
        }
    }

    const handleUpdateMiscItems = async (sortedList) => {
        try {
            await updateMiscItems({
                organisationId,
                quoteId,
                miscItems: sortedList,
            }).unwrap()
        } catch (error) {
            notifyMutationError(error, t('Failed to reorder $t(quote) items. Undoing changes'))
        }
    }

    const handleCriteriaChange = async (event) => {
        const { value } = event.target
        const previous = selectedCriteria
        setSelectedCriteria(value)

        const sortedList = [...miscItems]
            .sort(getSortFunction(value, selectedDirection))
            .map((miscItem, index) => ({ ...miscItem, index }))

        // Roll back if the quote never persisted, so the dropdown cannot keep
        // showing a sort order the server rejected.
        if (!(await handleUpdateQuote({ sortMiscItemsBy: value }))) {
            setSelectedCriteria(previous)
            return
        }

        handleUpdateMiscItems(sortedList)
    }

    const handleDirectionChange = async (event) => {
        const { value } = event.target
        const previous = selectedDirection
        setSelectedDirection(value)

        const sortedList = [...miscItems]
            .sort(getSortFunction(selectedCriteria, value))
            .map((miscItem, index) => ({ ...miscItem, index }))

        if (!(await handleUpdateQuote({ sortMiscItemsAsc: value == 'ASC' }))) {
            setSelectedDirection(previous)
            return
        }

        handleUpdateMiscItems(sortedList)
    }

    return (
        <>
            <FormControl variant="outlined">
                <InputLabel
                    data-testid="sort-misc-by-label"
                    id="selected-misc-criteria-label"
                >
                    {t('Sort by')}
                </InputLabel>
                <Select
                    data-testid="sort-by"
                    id="selected-misc-criteria"
                    label={t('Sort by')}
                    labelId="selected-misc-criteria-label"
                    size="small"
                    value={selectedCriteria}
                    onChange={handleCriteriaChange}
                >
                    <MenuItem value={'Default'}>{t('Upload order')}</MenuItem>
                    {selectedCriteria === 'Custom' ? <MenuItem value={'Custom'}>{t('User defined')}</MenuItem> : null}
                    <MenuItem value={'Quantity'}>{t('Quantity')}</MenuItem>
                    <MenuItem value={'UnitPrice'}>{t('Unit price')}</MenuItem>
                    <MenuItem value={'Name'}>{t('Item name')}</MenuItem>
                </Select>
            </FormControl>
            <FormControl variant="outlined">
                <InputLabel
                    data-testid="order-misc-by-label"
                    id="selected-misc-direction-label"
                >
                    {t('Order by')}
                </InputLabel>
                <Select
                    data-testid="order-by"
                    disabled={selectedCriteria === 'Custom'}
                    id="selected-misc-direction"
                    label={t('Order by')}
                    labelId="selected-misc-direction-label"
                    size="small"
                    value={selectedDirection}
                    onChange={handleDirectionChange}
                >
                    <MenuItem value={'ASC'}>{t('ASC')}</MenuItem>
                    <MenuItem value={'DESC'}>{t('DESC')}</MenuItem>
                </Select>
            </FormControl>
        </>
    )
}

export default MiscItemListSort
